import { useState, useEffect } from "react";
import axios from "axios";
import {
  Quotes, Factory, Coins, Megaphone, VideoCamera, Lightbulb, Target,
  CheckCircle, Warning, Storefront, ArrowRight, CaretDown, Copy, ArrowsClockwise, Spinner,
} from "@phosphor-icons/react";
import { buildGenericDetail } from "./commerceData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SampleTag = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] text-violet-600 bg-violet-50 ring-1 ring-violet-200 rounded-full px-2 py-0.5" data-testid="commerce-sample-tag">
    Sample Intelligence
  </span>
);

const Block = ({ title, icon: Icon, children, testid }) => (
  <div className="mt-6" data-testid={testid}>
    <h3 className="flex items-center gap-2 text-sm font-bold text-zinc-900 mb-3">
      {Icon && <Icon size={16} weight="duotone" className="text-violet-600" />} {title}
    </h3>
    {children}
  </div>
);

const InfoCard = ({ title, icon: Icon, rows, testid }) => (
  <div className="rounded-2xl bg-white/80 backdrop-blur border border-violet-100 p-4 shadow-sm" data-testid={testid}>
    <h4 className="flex items-center gap-2 text-sm font-bold text-zinc-900 mb-2">
      {Icon && <Icon size={15} weight="duotone" className="text-violet-600" />} {title}
    </h4>
    {rows.map(([label, value]) => (
      <div key={label} className="flex items-start justify-between gap-3 py-1.5 border-t border-zinc-100 first:border-t-0">
        <span className="text-xs text-zinc-500">{label}</span>
        <strong className="text-xs font-semibold text-zinc-800 text-right">{value}</strong>
      </div>
    ))}
  </div>
);

const Mini = ({ label, value }) => (
  <div className="rounded-xl bg-white border border-violet-100 px-3 py-2.5">
    <div className="text-[11px] text-zinc-500">{label}</div>
    <div className="text-sm font-bold text-zinc-900">{value}</div>
  </div>
);

const inlineMd = (text) =>
  text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i} className="font-bold text-zinc-900">{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) return <em key={i} className="italic text-zinc-600">{p.slice(1, -1)}</em>;
    return <span key={i}>{p}</span>;
  });

const MarkdownLite = ({ text }) => {
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-1">
      {lines.map((raw, i) => {
        const t = raw.trimEnd();
        const ts = t.trimStart();
        if (!t.trim()) return <div key={i} className="h-1.5" />;
        if (/^([-_*]\s?){3,}$/.test(t.trim())) return <hr key={i} className="my-2.5 border-zinc-100" />;
        if (t.startsWith("### ")) return <h5 key={i} className="text-sm font-bold text-zinc-900 mt-2">{inlineMd(t.slice(4))}</h5>;
        if (t.startsWith("## ")) return <h4 key={i} className="text-[15px] font-bold text-zinc-900 mt-2">{inlineMd(t.slice(3))}</h4>;
        if (t.startsWith("# ")) return <h3 key={i} className="text-base font-extrabold text-zinc-900 mt-2">{inlineMd(t.slice(2))}</h3>;
        if (ts.startsWith("> ")) return (
          <div key={i} className="border-l-2 border-violet-300 pl-3 py-0.5 text-sm text-zinc-600">{inlineMd(ts.slice(2))}</div>
        );
        if (/^\d+\.\s+/.test(ts)) return (
          <div key={i} className="flex gap-2 text-sm text-zinc-700 pl-1">
            <span className="text-violet-600 font-semibold">{ts.match(/^(\d+)\./)[1]}.</span>
            <span>{inlineMd(ts.replace(/^\d+\.\s+/, ""))}</span>
          </div>
        );
        if (/^[-*]\s+/.test(ts)) return (
          <div key={i} className="flex gap-2 text-sm text-zinc-700 pl-1">
            <span className="text-violet-500 mt-0.5">•</span><span>{inlineMd(ts.replace(/^[-*]\s+/, ""))}</span>
          </div>
        );
        return <p key={i} className="text-sm text-zinc-700 leading-relaxed">{inlineMd(t)}</p>;
      })}
    </div>
  );
};

const scoreTone = (badge) =>
  badge === "Investigate" ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
  : badge === "Watch" ? "bg-amber-50 text-amber-700 ring-amber-200"
  : "bg-rose-50 text-rose-700 ring-rose-200";

export const StrategyCard = ({ strategy }) => (
  <div className="mt-7 rounded-3xl p-6 sm:p-7 text-white shadow-[0_22px_70px_-20px_rgba(76,29,149,0.55)]"
    style={{ background: "linear-gradient(135deg,#1e1636 0%,#4c2a86 100%)" }} data-testid="commerce-strategy-card">
    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-violet-300 flex items-center gap-2">
      <Lightbulb size={14} weight="fill" /> AI Strategy
    </div>
    <h3 className="mt-3 text-xl sm:text-2xl font-extrabold tracking-tight text-white">{strategy.dont}</h3>
    <p className="mt-3 text-violet-100 leading-relaxed text-sm">
      {strategy.doText} <span className="text-white font-semibold">Target:</span> {strategy.target}{" "}
      <span className="text-white font-semibold">Differentiation:</span> {strategy.differentiation}
    </p>
    <div className="mt-5 inline-flex items-start gap-2 rounded-2xl bg-white/10 ring-1 ring-white/15 px-4 py-3">
      <ArrowRight size={16} weight="bold" className="text-violet-200 mt-0.5 flex-shrink-0" />
      <span className="text-sm font-semibold">Launch angle: “{strategy.angle}”</span>
    </div>
  </div>
);

export const LaunchCenter = ({ craving, marketName, detail }) => {
  const ACTIONS = [
    { kind: "landing_page", label: "Generate Landing Page" },
    { kind: "meta_ad", label: "Generate Meta Ads" },
    { kind: "tiktok_ad", label: "Generate TikTok Ads" },
    { kind: "ugc_script", label: "Generate UGC Script" },
    { kind: "higgsfield_prompt", label: "Generate Higgsfield Prompt" },
    { kind: "supplier_rfq", label: "Generate Supplier RFQ" },
  ];
  const [results, setResults] = useState({});   // kind -> {label, content}
  const [loading, setLoading] = useState({});    // kind -> bool
  const [errors, setErrors] = useState({});       // kind -> string
  const [open, setOpen] = useState({});            // kind -> bool
  const [copied, setCopied] = useState(null);

  // New craving selected → clear generated assets.
  useEffect(() => {
    setResults({}); setLoading({}); setErrors({}); setOpen({}); setCopied(null);
  }, [craving?.id]);

  const generate = async (kind, label) => {
    setLoading((s) => ({ ...s, [kind]: true }));
    setErrors((s) => ({ ...s, [kind]: null }));
    try {
      const { data } = await axios.post(`${API}/commerce/generate`, {
        kind,
        market: marketName,
        craving: {
          title: craving.title, score: craving.score, badge: craving.badge,
          frequency: craving.frequency, growth: craving.growth,
          intent: craving.intent, competition: craving.competition,
        },
        detail,
      });
      setResults((s) => ({ ...s, [kind]: { label: data.label || label, content: data.content } }));
      setOpen((s) => ({ ...s, [kind]: true }));
    } catch (e) {
      setErrors((s) => ({ ...s, [kind]: e?.response?.data?.detail || "Generation failed. Try again." }));
    } finally {
      setLoading((s) => ({ ...s, [kind]: false }));
    }
  };

  const copy = (kind, text) => {
    navigator.clipboard?.writeText(text);
    setCopied(kind);
    setTimeout(() => setCopied((c) => (c === kind ? null : c)), 1500);
  };

  return (
    <div className="mt-6" data-testid="commerce-launch-center">
      <h3 className="flex items-center gap-2 text-sm font-bold text-zinc-900 mb-1">
        <Storefront size={16} weight="duotone" className="text-violet-600" /> Launch Center
      </h3>
      <p className="text-[11px] text-zinc-400 mb-3">Generated live from this craving's demand intelligence — customer voice, gaps, economics & ad signals.</p>
      <div className="flex flex-wrap gap-2.5">
        {ACTIONS.map(({ kind, label }) => {
          const busy = !!loading[kind];
          const done = !!results[kind];
          return (
            <button key={kind} onClick={() => generate(kind, label)} disabled={busy}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60 shadow-[0_10px_24px_-8px_rgba(139,92,246,0.7)] ${done ? "ring-2 ring-violet-300" : ""}`}
              style={{ background: "linear-gradient(90deg,#7c4dff,#a855f7)" }}
              data-testid={`commerce-launch-${kind}`}>
              {busy ? <Spinner size={13} className="animate-spin" weight="bold" /> : (done ? <ArrowsClockwise size={13} weight="bold" /> : null)}
              {busy ? "Generating…" : (done ? label.replace("Generate", "Regenerate") : label)}
            </button>
          );
        })}
      </div>

      {/* Expandable result cards */}
      <div className="mt-4 space-y-3" data-testid="commerce-launch-results">
        {ACTIONS.filter((a) => results[a.kind] || errors[a.kind]).map(({ kind, label }) => {
          const res = results[kind];
          const err = errors[kind];
          const isOpen = open[kind];
          return (
            <div key={kind} className="rounded-2xl border border-violet-100 bg-white/85 backdrop-blur overflow-hidden" data-testid={`commerce-result-${kind}`}>
              <button onClick={() => setOpen((s) => ({ ...s, [kind]: !s[kind] }))}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                data-testid={`commerce-result-toggle-${kind}`}>
                <span className="text-sm font-bold text-zinc-900">{res?.label || label.replace("Generate ", "")}</span>
                <span className="flex items-center gap-2">
                  {res && (
                    <span onClick={(e) => { e.stopPropagation(); copy(kind, res.content); }}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 hover:text-violet-800" data-testid={`commerce-result-copy-${kind}`}>
                      <Copy size={13} weight="duotone" /> {copied === kind ? "Copied" : "Copy"}
                    </span>
                  )}
                  <CaretDown size={14} weight="bold" className={`text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-zinc-100 pt-3">
                  {err
                    ? <p className="text-sm text-rose-600">{err}</p>
                    : <div className="commerce-md"><MarkdownLite text={res.content} /></div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OpportunityPanel = ({ craving, marketName }) => {
  const d = craving.detail || buildGenericDetail(craving, marketName);
  return (
    <div className="rounded-3xl bg-white/70 backdrop-blur-xl border border-violet-100 p-5 sm:p-7 shadow-[0_20px_60px_-24px_rgba(80,70,140,0.35)]" data-testid="commerce-opportunity-panel">
      <div className="flex items-start justify-between gap-4 pb-5 border-b border-zinc-100">
        <div>
          <SampleTag />
          <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-zinc-900" data-testid="commerce-opportunity-title">{craving.title}</h2>
          <p className="mt-1 text-sm text-zinc-500">{marketName} · {craving.frequency} frequency · {craving.growth} growth</p>
        </div>
        <div className="text-center flex-shrink-0">
          <div className="text-3xl font-black text-zinc-900 leading-none">{craving.score}</div>
          <div className={`mt-1.5 inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${scoreTone(craving.badge)}`}>{craving.badge}</div>
        </div>
      </div>

      <Block title="A. Customer Voice" icon={Quotes} testid="commerce-block-voice">
        <div className="grid sm:grid-cols-2 gap-2.5">
          {d.customerVoice.map((q, i) => (
            <div key={i} className="rounded-2xl bg-violet-50/60 border border-violet-100 px-4 py-3 text-sm text-zinc-600 italic">“{q}”</div>
          ))}
        </div>
      </Block>

      <Block title="B. Demand Validation" icon={CheckCircle} testid="commerce-block-demand">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {d.demand.map(([l, v]) => <Mini key={l} label={l} value={v} />)}
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-3 py-1">
          Demand status: {d.demandStatus}
        </div>
      </Block>

      <Block title="C. Existing Solutions" icon={Storefront} testid="commerce-block-solutions">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {d.solutions.map(([l, v]) => <Mini key={l} label={l} value={v} />)}
        </div>
      </Block>

      <Block title="D. Market Leaders" testid="commerce-block-leaders">
        <div className="flex flex-wrap gap-2">
          {d.leaders.map((b) => (
            <span key={b} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-zinc-100 text-zinc-700">{b}</span>
          ))}
        </div>
      </Block>

      <Block title="E. What Customers Hate" icon={Warning} testid="commerce-block-hate">
        <div className="flex flex-wrap gap-2">
          {d.hates.map((h) => (
            <span key={h} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-600 ring-1 ring-rose-100">{h}</span>
          ))}
        </div>
      </Block>

      <Block title="F. Opportunity Gaps" icon={Target} testid="commerce-block-gaps">
        <div className="space-y-2">
          {d.gaps.map((g, i) => (
            <div key={i} className="flex items-start gap-2.5 rounded-2xl bg-white border border-violet-100 px-4 py-3">
              <span className="mt-0.5 text-[10px] font-black text-violet-600 bg-violet-50 rounded-md px-1.5 py-0.5">GAP {i + 1}</span>
              <span className="text-sm text-zinc-700">{g}</span>
            </div>
          ))}
        </div>
      </Block>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <InfoCard title="G. Manufacturer Intelligence" icon={Factory} rows={d.manufacturing} testid="commerce-card-manufacturing" />
        <InfoCard title="H. Economics" icon={Coins} rows={d.economics} testid="commerce-card-economics" />
        <InfoCard title="I. Meta Intelligence" icon={Megaphone} rows={d.meta} testid="commerce-card-meta" />
        <InfoCard title="J. TikTok Intelligence" icon={VideoCamera} rows={d.tiktok} testid="commerce-card-tiktok" />
      </div>

      <StrategyCard strategy={d.strategy} />
      <LaunchCenter craving={craving} marketName={marketName} detail={d} />
    </div>
  );
};

export default OpportunityPanel;
